/**
 * Google Tag Manager form tracking
 * Listens for form submission events and pushes to dataLayer
 * Consent is already checked server-side before events are dispatched
 * Server-side components only dispatch these events if analytics consent is given
 */

// Push event to GTM dataLayer
function pushToDataLayer(data) {
    if (typeof window.dataLayer === 'undefined') {
        window.dataLayer = [];
    }

    // Push to dataLayer - GTM will respect consent mode settings
    // Note: Server-side already verified consent before dispatching this event
    window.dataLayer.push(data);
}

function collectUserFields(source, userFields, result) {
    if (!source || typeof source !== 'object') {
        return;
    }

    userFields.forEach((field) => {
        const value = source[field];

        if (typeof value === 'string' && value.trim() !== '') {
            result[field] = value;
        }
    });
}

function attachUserData(payload, data) {
    const userFields = ['name', 'email', 'phone', 'full_name', 'first_name', 'last_name'];
    const userData = {};

    collectUserFields(data, userFields, userData);

    if (Array.isArray(data)) {
        data.forEach((entry) => {
            collectUserFields(entry, userFields, userData);
        });
    }

    if (data && typeof data === 'object') {
        Object.values(data).forEach((entry) => {
            collectUserFields(entry, userFields, userData);
        });
    }

    if (Object.keys(userData).length > 0) {
        // Map full_name to name for GTM (GTM expects 'name' as user property)
        if (userData.full_name && !userData.name) {
            userData.name = userData.full_name;
        }

        // Flatten user data to top level for GTM User Properties
        // GTM expects name, email, phone at the top level, not nested in user_data
        return {
            ...payload,
            ...userData, // Spread user properties to top level for GTM
            user_data: userData, // Keep nested for Facebook Pixel compatibility
        };
    }

    return payload;
}

// Helper to extract data from Livewire event (handles both array and object formats)
function extractLivewireData(data) {
    // Livewire sometimes sends data as an array, extract the first element if so
    if (Array.isArray(data) && data.length > 0) {
        return data[0];
    }
    return data;
}

// Helper to exclude user fields from event data (they should only be in user_data)
function excludeUserFields(data) {
    const userFields = ['name', 'email', 'phone', 'full_name', 'first_name', 'last_name'];
    const filtered = {...data};
    userFields.forEach((field) => {
        delete filtered[field];
    });
    return filtered;
}

// Build Facebook Pixel advanced matching params from user_data (em, ph, fn, ln)
function fbqAdvancedMatching(userData) {
    if (!userData || typeof userData !== 'object') return {};
    const params = {};
    if (userData.email) params.em = userData.email.toLowerCase().trim();
    if (userData.phone) {
        const ph = userData.phone.replace(/\D/g, '');
        if (ph) params.ph = ph;
    }
    if (userData.name) {
        const parts = userData.name.trim().split(/\s+/);
        if (parts.length > 0) params.fn = parts[0];
        if (parts.length > 1) params.ln = parts.slice(1).join(' ');
    }
    return params;
}

// Listen for Livewire events
document.addEventListener('livewire:init', () => {
    // Contact form: one event from Livewire; payload includes user data only when marketing consent
    Livewire.on('contact-form-submitted', (data) => {
        const eventData = extractLivewireData(data) || {};
        console.log('[GTM] contact-form-submitted – data from Livewire:', eventData);

        const gtmEvent = {
            event: 'contact_form_submit',
            form_name: eventData.form_name || 'contact',
            form_location: window.location.pathname,
            event_category: 'form',
            event_action: 'submit',
            event_label: 'contact_form',
            page_title: document.title,
            page_location: window.location.href,
            ...excludeUserFields(eventData),
        };

        const finalEvent = attachUserData(gtmEvent, eventData);
        console.log('[GTM] contact_form_submit – payload sent to dataLayer:', finalEvent);
        pushToDataLayer(finalEvent);

        if (finalEvent.user_data && Object.keys(finalEvent.user_data).length > 0 && typeof fbq !== 'undefined') {
            fbq('track', 'Lead', {
                content_name: 'Contact Form Submission',
                content_category: 'Contact Form',
            }, fbqAdvancedMatching(finalEvent.user_data));
            fbq('trackCustom', 'ContactFormSubmitted', finalEvent.user_data);
        }
    });

    // Corporate gifts form: one event from Livewire; payload includes user data only when marketing consent
    Livewire.on('corporate-gifts-form-submitted', (data) => {
        const eventData = extractLivewireData(data) || {};
        console.log('[GTM] corporate-gifts-form-submitted – data from Livewire:', eventData);

        const gtmEvent = {
            event: eventData.event || 'corporate_gifts_form_submit',
            form_name: eventData.form_name || 'corporate_gifts',
            form_location: window.location.pathname,
            event_category: 'form',
            event_action: 'submit',
            event_label: 'corporate_gifts_form',
            page_title: document.title,
            page_location: window.location.href,
            ...excludeUserFields(eventData),
        };

        const finalEvent = attachUserData(gtmEvent, eventData);
        console.log('[GTM] corporate_gifts_form_submit – payload prepared for dataLayer:', finalEvent);

        if (finalEvent.user_data && Object.keys(finalEvent.user_data).length > 0 && typeof fbq !== 'undefined') {
            fbq('track', 'Lead', {
                content_name: 'Corporate Gifts Form Submission',
                content_category: 'Corporate Gifts Form',
            }, fbqAdvancedMatching(finalEvent.user_data));
            fbq('trackCustom', 'CorporateGiftsFormSubmitted', finalEvent.user_data);
        }
    });

    // Listen for fees page search (debounced on input; consent checked server-side)
    Livewire.on('fees-search-performed', (data) => {
        const payload = extractLivewireData(data);
        pushToDataLayer({
            event: 'fees_search',
            search_term: payload?.searchTerm ?? payload?.search_term ?? '',
            search_location: payload?.searchLocation ?? payload?.search_location ?? 'fees_page',
        });
    });
});
